import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ReactComponent as BackArrowIcon } from '../../assets/icons/arrow-left.svg';
import { ReactComponent as RejectIcon } from '../../assets/icons/close-circle-red.svg';
import { ReactComponent as DeleteIcon } from '../../assets/icons/delete-icon-red.svg';
import { ReactComponent as DocumentFile } from '../../assets/icons/document-file.svg';
import { ReactComponent as EditPencilCard } from '../../assets/icons/edit-pencil.svg';
import { ReactComponent as EyeIcon } from '../../assets/icons/eye-icon.svg';
import { ReactComponent as VerifyIcon } from '../../assets/icons/tick-circle-icon.svg';
import profiles from '../../assets/images/profiles/profile.jpg';
import {
  AccessFacilityPortal,
  AccountsInterFace,
} from '../../interface/account-management.interface';
import DeleteAccount from '../../pages/account-management/components/delete-account';
import RejectAccount from '../../pages/account-management/components/reject-account';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { fetchAllAccounts } from '../../redux/slices/AccountManagementSlice';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import { RootState } from '../../redux/store';
import { changeDateFormat } from '../../shared-functions/DateTime';
import ToFixedValue from '../../shared-functions/ToFixedValue';
import accountApi from '../../utils/api/account';
import authApi from '../../utils/api/auth';
import MyButton from '../button/Button';
import CustomFileInput from '../custom-file-input/CustomFileInput';
import CustomInput from '../custom-input/CustomInput';
import CustomPhoneInput from '../custom-input/CustomPhoneInput';
import ConfirmBox from '../modal-box/ConfirmBox';
import ModalBox from '../modal-box/ModalBox';
import SuccessNotification from '../success-notification/SuccessNotification';

interface FacilityDetailsSidePanelProperties {
  fetchIdFrom: 'queryParams' | 'props';
  id?: string;
  isEditMode?: boolean;
  onEditModeChange?: (state: boolean) => void;
  actionActive?: boolean;
}

/**
 * Open the document in new tap function
 * @param pdfUrl
 */
const openPdf = (pdfUrl: string) => {
  if (pdfUrl) window.open(pdfUrl, '_blank');
};

/**
 * Facility details side panel component
 * @param properties
 * @returns
 */
function FacilityDetailsSidePanel(properties: FacilityDetailsSidePanelProperties) {
  const { t, i18n } = useTranslation('account');
  const dispatch = useAppDispatch();
  const { onEditModeChange, fetchIdFrom, id, actionActive } = properties;
  const [searchParameters] = useSearchParams();
  const { page, limit, accounts } = useAppSelector((state: RootState) => state.accounts);
  const [accountData, setAccountData] = useState<AccountsInterFace | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  // Modals state
  const [modalView, setModalView] = useState({
    status: false,
    title: '',
    content: <div />,
    header: false,
    rightCorner: '',
    notification: false,
  });

  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState<any>({}); // Use specific type if possible, or AccountsInterFace partial

  const [confirmBox, setConfirmBox] = useState({
    status: false,
    title: '',
    subTitle: '',
    submitLabel: `${t('ok')}`,
    /**
     * Confirm submit function
     */
    conformSubmit: () => {},
  });

  /**
   * Show success notification
   * @param message
   */
  const showSuccessNotification = (message: string) => {
    setModalView({
      status: true,
      title: '',
      header: false,
      rightCorner: '',
      notification: true,
      content: (
        <SuccessNotification
          title={message}
          description=""
          notificationCloseTimer={3000}
          onNotificationClose={() => setModalView((previous) => ({ ...previous, status: false }))}
        />
      ),
    });
  };

  /**
   * Show success notification
   * @param message
   */

  /**
   * Fetch facility details from API
   * @param facilityId
   */
  const fetchDetails = (facilityId: string) => {
    setDetailsLoading(true);
    accountApi
      .fetchOneAccounts(facilityId)
      .then((response: any) => {
        setAccountData((previous: AccountsInterFace | null) => {
          // Create a new object merging previous data (from redux) and new data (from api)
          // Prioritize API data, but keep stats from Redux if API doesn't provide them
          const newData = { ...previous, ...response.data };

          // Ensure stats are preserved if they exist in previous but not/empty in response
          if (previous) {
            if (!newData.stationCount && previous.stationCount)
              newData.stationCount = previous.stationCount;
            if (!newData.totalDeviceCount && previous.totalDeviceCount)
              newData.totalDeviceCount = previous.totalDeviceCount;
            if (!newData.activeDeviceCount && previous.activeDeviceCount)
              newData.activeDeviceCount = previous.activeDeviceCount;
            if (!newData.powerConsumed && previous.powerConsumed)
              newData.powerConsumed = previous.powerConsumed;
          }
          return newData;
        });
        setDetailsLoading(false);
      })
      .catch((error: any) => {
        setDetailsLoading(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message || 'Failed to fetch details',
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  // Initial load
  useEffect(() => {
    let facilityId = id;
    if (fetchIdFrom === 'queryParams') {
      const facilityFromQuery =
        searchParameters.get('facilityId') || searchParameters.get('tenantId');

      facilityId = facilityFromQuery || undefined;
    }

    if (facilityId) {
      const found = accounts.find(
        (a: AccountsInterFace) =>
          a.id === facilityId || a._id === facilityId || a.tenantId === facilityId,
      );
      if (found) {
        setAccountData(found);
      }
      fetchDetails(facilityId);
    }
  }, [fetchIdFrom, id, searchParameters, accounts]);

  /**
   * Refresh the accounts list
   */
  const refreshList = () => {
    dispatch(fetchAllAccounts({ page, limit }));
  };

  /**
   * Handle Verify Account
   */
  const handleVerify = () => {
    if (!accountData?.tenantId) return;

    setConfirmBox({
      status: true,
      title: t('verifyAccountTitle'),
      subTitle: t('verifyAccountSubTitle'),
      submitLabel: t('verify'),
      /**
       * Confirm submit function
       */
      conformSubmit: () => {
        accountApi
          .approvedAccounts(accountData.tenantId, { submissionStatus: 'approved' })
          .then(() => {
            setConfirmBox((previous) => ({ ...previous, status: false }));
            showSuccessNotification(t('accountApprovedSuccessfully'));
            refreshList();
            // update local state
            setAccountData((previous: AccountsInterFace | null) =>
              previous
                ? {
                    ...previous,
                    status: 'approved',
                    submission: { ...previous.submission, status: 'approved' },
                  }
                : null,
            );
            // Also wait a bit then close the panel or just refresh
          })
          .catch((error: any) => {
            dispatch(
              setNotificationMessage({
                message: error?.response?.data?.message,
                status: true,
                type: 'error',
                code: 0,
              }),
            );
          });
      },
    });
  };

  /**
   * Handle Reject Account
   */
  const handleReject = () => {
    if (!accountData) return;

    setModalView({
      status: true,
      title: '',
      header: false,
      rightCorner: '',
      notification: false,
      content: (
        <RejectAccount
          accountDetails={{
            accountName: accountData.accountName || '',
            status: accountData.status,
            name: accountData.name,
            governmentId: [],
            email: accountData.email,
            phone: accountData.phoneNumber || '',
            address: accountData.address || '',
            password: '',
            countryCode: accountData.countryCode || '',
            rePassword: '',
          }}
          closeModal={() => setModalView((previous) => ({ ...previous, status: false }))}
          conformSubmit={(value: any) => {
            if (accountData.tenantId) {
              accountApi
                .approvedAccounts(accountData.tenantId, {
                  reason: value.reason,
                  submissionStatus: 'rejected',
                })
                .then(() => {
                  setModalView((previous) => ({ ...previous, status: false }));
                  showSuccessNotification(t('accountRejectedSuccessfully'));
                  refreshList();
                  setAccountData((previous: AccountsInterFace | null) =>
                    previous
                      ? {
                          ...previous,
                          status: 'rejected',
                          submission: { ...previous.submission, status: 'rejected' },
                        }
                      : null,
                  );
                })
                .catch((error: any) => {
                  dispatch(
                    setNotificationMessage({
                      message: error?.response?.data?.message,
                      status: true,
                      type: 'error',
                      code: 0,
                    }),
                  );
                });
            }
          }}
        />
      ),
    });
  };

  /**
   * Handle Reset Password
   */
  const handleResetPassword = () => {
    if (!accountData?.tenantId) return;

    setConfirmBox({
      status: true,
      title: t('resetPasswordTitle'),
      subTitle: t('resetPasswordSubTitle'),
      submitLabel: t('proceed'),
      /**
       * Conform submit function
       */
      conformSubmit: () => {
        accountApi
          .facilityRestPassword(accountData.tenantId, { submissionStatus: 'resetPassword' })
          .then(() => {
            setConfirmBox((previous) => ({ ...previous, status: false }));
            showSuccessNotification(t('passwordResetLinkSent'));
          })
          .catch((error: any) => {
            dispatch(
              setNotificationMessage({
                message: error?.response?.data?.message,
                status: true,
                type: 'error',
                code: 0,
              }),
            );
          });
      },
    });
  };

  /**
   * View Facility Function
   */
  const viewFacilityFunction = (currentData: any) => {
    const accountDetails = accounts.find(
      (value: AccountsInterFace) => value.tenantId === currentData,
    );
    if (accountDetails && Object.keys(accountDetails).length > 0) {
      const request: AccessFacilityPortal = {
        tenantId: accountDetails.tenantId,
      };

      authApi
        .accessFacilityPortal(request)
        .then((response) => {
          const responseData = response?.data;
          const { origin } = window;

          const url = new URL(origin);

          if (url.hostname.includes('admin')) {
            url.hostname = url.hostname.replace('admin', 'facility');
          } else if (url.hostname.includes('cpo')) {
            url.hostname = url.hostname.replace('cpo', 'partner');
          }

          const updatedOrigin = url.origin;
          window.open(
            `${updatedOrigin}/login/credentials?accessToken=${responseData.access.token}&accessTokenExpires=${responseData.access.expires}&refreshToken=${responseData.refresh.token}&refreshTokenExpires=${responseData.refresh.expires}&isEmailVerified=${responseData.isEmailVerified}&isPhoneNumberVerified=${responseData.isPhoneNumberVerified}&isInitialPasswordReset=${responseData.isInitialPasswordReset}&accountVerificationStatus=${responseData.accountVerificationStatus}`,
            '_blank',
          );
        })
        .catch((error) => {
          dispatch(
            setNotificationMessage({
              message: error?.response?.data?.message,
              status: true,
              type: 'error',
              code: error?.response?.data?.code,
            }),
          );
        });
    }
  };

  /**
   * Handle Delete Account
   */
  const handleDelete = () => {
    if (!accountData) return;

    setModalView({
      status: true,
      title: '',
      header: false,
      rightCorner: '',
      notification: false,
      content: (
        <DeleteAccount
          accountDetails={accountData}
          closeModal={() => setModalView((previous) => ({ ...previous, status: false }))}
          conformSubmit={() => {
            if (accountData.tenantId) {
              accountApi
                .deleteAccount(accountData.tenantId)
                .then(() => {
                  setModalView((previous) => ({ ...previous, status: false }));
                  showSuccessNotification(t('accountDeletedSuccessfully'));
                  refreshList();
                })
                .catch((error: any) => {
                  dispatch(
                    setNotificationMessage({
                      message: error?.response?.data?.message,
                      status: true,
                      type: 'error',
                      code: 0,
                    }),
                  );
                });
            }
          }}
          buttonLabel={t('blockAccount')}
        />
      ),
    });
  };

  /**
   * Toggle Edit Mode
   */
  const toggleEditMode = () => {
    if (editMode) {
      // Cancelling edit mode
      setEditMode(false);
      setEditData({});
      if (onEditModeChange) onEditModeChange(false);
    } else {
      // Entering edit mode - initialize data
      setEditData({
        accountName: accountData?.accountName || '',
        name: accountData?.name || '',
        email: accountData?.email || '',
        phoneNumber: accountData?.phoneNumber || '',
        countryCode: accountData?.countryCode || '1',
        governmentIdOld: (accountData as any)?.governmentId
          ? [(accountData as any).governmentId]
          : [], // Assuming API handles it this way or needs adjustment
      });
      setEditMode(true);
      if (onEditModeChange) onEditModeChange(true);
    }
  };

  /**
   * Handle Input Change
   */
  const handleInputChange = (name: string, value: string | number | any) => {
    if (name === 'governmentId') {
      setEditData((previous: any) => ({
        ...previous,
        [name]: value,
        governmentIdOld: [],
      }));
    } else {
      setEditData((previous: any) => ({
        ...previous,
        [name]: value,
      }));
    }
  };

  /**
   * Handle Edit Submit
   */
  const handleEditSubmit = () => {
    if (!accountData?.tenantId) return;
    // Prepare payload - handle file upload separately if needed or as part of payload
    // Assuming editAccounts handles the payload structure
    const payload: any = {};
    if (editData?.name) {
      payload.name = editData?.name;
    }
    if (editData?.accountName) {
      payload.accountName = editData?.accountName;
    }
    if (editData?.email) {
      payload.email = editData?.email;
    }
    if (editData?.phoneNumber && editData?.countryCode) {
      payload.phoneNumber = editData?.phoneNumber;
      payload.countryCode = editData?.countryCode;
    }
    if (editData?.governmentId?.[0]) {
      payload.governmentId = editData?.governmentId?.[0];
    }

    // If governmentId is an array of files/objects, backend might expect a specific format
    // Adjust payload logic based on API requirements.
    // Here we'll pass it as is or handle file upload logic if your createAccounts does it.
    // For now, assuming straightforward update.

    // NOTE: If file upload logic is complex (like uploading first to get URL), add that here.
    // CustomFileInput returns File[] usually.

    accountApi
      .editAccounts(accountData.tenantId, payload)
      .then(() => {
        showSuccessNotification(t('accountUpdatedSuccessfully') || 'Facility updated successfully');
        setEditMode(false);
        if (onEditModeChange) onEditModeChange(false);
        refreshList();
        // Optimistically update local data or re-fetch
        fetchDetails(accountData.tenantId);
      })
      .catch((error: any) => {
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: 0,
          }),
        );
      });
  };

  if (!accountData && !detailsLoading) return <div className="no-data">{t('noDataFound')}</div>;

  return (
    <div className="facility-details-side-panel" dir={i18n.dir()}>
      <ModalBox
        open={modalView.status}
        title={modalView.title}
        content={modalView.content}
        closeModal={() => setModalView((previous) => ({ ...previous, status: false }))}
        header={modalView.header}
        rightCorner={modalView.rightCorner}
        notification={modalView.notification}
      />
      <ConfirmBox
        open={confirmBox.status}
        title={confirmBox.title}
        subTitle={confirmBox.subTitle}
        conformSubmit={confirmBox.conformSubmit}
        closeModal={() => setConfirmBox((previous) => ({ ...previous, status: false }))}
        submitLabel={confirmBox.submitLabel}
      />

      <div className="panel-header">
        {editMode && (
          <div className="edit-close">
            <BackArrowIcon onClick={toggleEditMode} />
          </div>
        )}
        <h3>{editMode ? t('editFacility') : t('facilityDetails')}</h3>
        <div className="text-primary" role="presentation">
          {actionActive && (
            <div className="edit-delete">
              {!editMode && <EditPencilCard onClick={toggleEditMode} />}
              {false && <DeleteIcon onClick={handleDelete} />}
            </div>
          )}
        </div>
      </div>
      <div
        style={{
          overflowY: 'scroll',
          height: '550px',
        }}>
        <div className="facility-details-group">
          <div className="facility-button">
            <div className="profile-card">
              <div className="profile-card-content">
                <div>
                  <img
                    src={(accountData as any)?.profileImage?.signedUrl || profiles}
                    alt="profile"
                  />
                  <div className="profile-info">
                    <div className="name">
                      <div>{accountData?.accountName || '-'}</div>
                      <div className="status-icon">
                        {accountData?.submission?.status === 'approved' && <VerifyIcon />}
                        {accountData?.submission?.status === 'rejected' && <RejectIcon />}
                      </div>
                    </div>
                    <div className="id">{accountData?.accountCustomId}</div>
                  </div>
                </div>
                {accountData?.submission?.status === 'approved' && actionActive && (
                  <div>
                    <MyButton
                      label={t('accessFacility')}
                      buttonType="primary"
                      onClickFunc={() => viewFacilityFunction(searchParameters.get('tenantId'))}
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="section basic-info">
              <h4>{t('basicInformation')}</h4>
              {editMode ? (
                <>
                  <div className="info-row-input">
                    <p className="label"> {t('accountName')}:</p>
                    <CustomInput
                      type="text"
                      name="accountName"
                      placeholder={t('accountName')}
                      value={editData.accountName}
                      onChangeValue={handleInputChange}
                      // inputLabel="accountName"
                    />
                  </div>
                  <div className="info-row-input">
                    <p className="label"> {t('userName')}:</p>
                    <CustomInput
                      type="text"
                      name="name"
                      placeholder={t('userName')}
                      value={editData.name}
                      onChangeValue={handleInputChange}
                    />
                  </div>
                  <div className="info-row-input">
                    <p className="label"> {t('email')}:</p>
                    <CustomInput
                      type="email"
                      name="email"
                      placeholder={t('email')}
                      value={editData.email}
                      onChangeValue={handleInputChange}
                    />
                  </div>
                  <div className="info-row-input">
                    <p className="label"> {t('phoneNumber')}:</p>
                    <CustomPhoneInput
                      type="number"
                      name="phoneNumber"
                      placeholder={t('phoneNumber')}
                      value={editData?.phoneNumber}
                      value1={editData?.countryCode}
                      onChangeValue={handleInputChange}
                    />
                  </div>
                  <div className="info-row">
                    <p className="label">{t('createdAt')}:</p>
                    <span>
                      {accountData?.createdAt ? changeDateFormat(accountData?.createdAt) : '-'}
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <div className="info-row">
                    <p className="label"> {t('accountName')}:</p>
                    <span>{accountData?.accountName || '-'}</span>
                  </div>
                  <div className="info-row">
                    <p className="label"> {t('userName')}:</p>
                    <span>{accountData?.name || '-'}</span>
                  </div>
                  <div className="info-row">
                    <p className="label">{t('email')}:</p>
                    <span>{accountData?.email || '-'}</span>
                  </div>
                  <div className="info-row">
                    <p className="label">{t('phone')}:</p>
                    <span>
                      {accountData?.countryCode ? `+${accountData?.countryCode} ` : ''}
                      {accountData?.phoneNumber || '-'}
                    </span>
                  </div>
                  <div className="info-row">
                    <p className="label">{t('createdAt')}:</p>
                    <span>
                      {accountData?.createdAt ? changeDateFormat(accountData?.createdAt) : '-'}
                    </span>
                  </div>
                </>
              )}
            </div>

            <div className="section stations">
              <h4>{t('stations')}</h4>
              <div className="stats-grid">
                <div className="stat-item">
                  <p className="label">{t('noOfStations')}</p>
                  <div className="value">{accountData?.stationCount || 0}</div>
                </div>
                <div className="stat-item">
                  <p className="label">{t('noOfChargers')}</p>
                  <div className="value">{accountData?.totalDeviceCount || 0}</div>
                </div>
                <div className="stat-item">
                  <p className="label">{t('onlineChargers')}</p>
                  <div className="value">{accountData?.activeDeviceCount || 0}</div>
                </div>
                <div className="stat-item">
                  <p className="label small">{t('kwhConsumedPaid')}</p>
                  <div className="value">
                    {ToFixedValue(Number(accountData?.powerConsumed) / 1000)} Kwh
                  </div>
                </div>
              </div>
            </div>

            <div className="section documents">
              <h4>{t('documents')}</h4>
              <p className="doc-label">{t('governmentId')}</p>
              {editMode ? (
                <CustomFileInput
                  acceptedFileType=".pdf,.jpg,.jpeg,.png"
                  placeholder={t('uploadHere')} // Or specific placeholder
                  name="governmentId"
                  selectedFiles={editData.governmentId} // You might need local state for new files if handling upload manually
                  existingFiles={editData.governmentIdOld} // Assuming editData.governmentId is MultiMediaData[]
                  onChangeValue={(name, file) => {
                    // For now just logging or simple set, but usually CustomFileInput needs File[]
                    // and we might need to handle 'newFile' vs 'existingFile' unselect logic
                    // For simplicity, just updating state, but ideally needs separate handling for new vs old
                    handleInputChange(name, file);
                  }}
                  unselectHandler={(name, url, index, type) => {
                    // Implement unselect logic to remove from editData.governmentId
                    if (type === 'existingFile') {
                      const updatedDocumentation = editData.governmentId.filter(
                        (_: any, index_: number) => index_ !== index,
                      );
                      handleInputChange('governmentId', updatedDocumentation);
                    }
                  }}
                />
              ) : (
                <div className="doc-list">
                  {(() => {
                    const governmentId = (accountData as any)?.governmentId;
                    if (governmentId?.signedUrl) {
                      return (
                        <div className="doc-item">
                          <div className="doc-icon">
                            <DocumentFile />
                            <span>{governmentId.fileName || t('governmentId')}</span>
                          </div>
                          <div
                            className="action"
                            role="button"
                            tabIndex={0}
                            onClick={() => openPdf(governmentId.signedUrl)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                openPdf(governmentId.signedUrl);
                              }
                            }}>
                            <EyeIcon />
                          </div>
                        </div>
                      );
                    }
                    if (governmentId?.fileName) {
                      return (
                        <div className="doc-item">
                          <DocumentFile />
                          <span>{governmentId.fileName}</span>
                          <div className="action">{/* No action if no URL */}</div>
                        </div>
                      );
                    }
                    return <div className="no-docs">-</div>;
                  })()}
                </div>
              )}
            </div>
          </div>
          {editMode ? (
            <div className="footer-actions-edit">
              <MyButton label={t('save')} buttonType="submit" onClickFunc={handleEditSubmit} />
            </div>
          ) : (
            <>
              {accountData?.submission?.status === 'pending' && (
                <div className="footer-actions">
                  <MyButton label={t('reject')} buttonType="error" onClickFunc={handleReject} />
                  <MyButton label={t('verify')} buttonType="submit" onClickFunc={handleVerify} />
                </div>
              )}
              {actionActive && accountData?.submission?.status === 'approved' && (
                <div className="footer-reset">
                  <MyButton
                    label={t('resetPassword')}
                    buttonType="outline"
                    onClickFunc={handleResetPassword}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default FacilityDetailsSidePanel;
